import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';
import { BarChart3, FileDown, Download, Loader2, Package2, Calendar as CalendarIcon, ShoppingBag } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const StatisticsExport = () => {
  const [loading, setLoading] = useState(false);
  const [exportType, setExportType] = useState('backshop'); // 'backshop' or 'mhd'
  
  // Backshop Export State
  const [backshopStartDate, setBackshopStartDate] = useState('');
  const [backshopEndDate, setBackshopEndDate] = useState('');
  const [backshopType, setBackshopType] = useState('both');
  const [backshopFormat, setBackshopFormat] = useState('summary');
  
  useEffect(() => {
    // Set default dates (current month)
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    setBackshopStartDate(firstDay.toISOString().split('T')[0]);
    setBackshopEndDate(lastDay.toISOString().split('T')[0]);
  }, []);

  const handleBackshopExport = async () => {
    if (!backshopStartDate || !backshopEndDate) {
      toast.error('Bitte wählen Sie einen Zeitraum');
      return;
    }
    
    if (new Date(backshopStartDate) > new Date(backshopEndDate)) {
      toast.error('Start-Datum muss vor End-Datum liegen');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await axios.post(
        `${API}/api/backshop/export`,
        {
          start_date: backshopStartDate,
          end_date: backshopEndDate,
          type: backshopType,
          format: backshopFormat
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      const exportData = response.data;
      
      // Download PDF immediately
      await downloadPDF(exportData.id);
      
      toast.success(`Export erstellt! ${exportData.total_quantity} Stück aus ${exportData.total_products} Produkten`);
      
    } catch (error) {
      console.error('Export failed:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unbekannter Fehler';
      toast.error(`Fehler beim Erstellen des Exports: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleMHDExport = async () => {
    setLoading(true);
    
    try {
      toast.info('PDF wird vorbereitet...');
      
      const response = await axios.get(
        `${API}/api/mhd/writeoffs/export-pdf`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          responseType: 'blob',
          timeout: 30000
        }
      );
      
      if (!response.data || response.data.size === 0) {
        throw new Error('PDF ist leer');
      }
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `MHD-Abschriften-${new Date().toISOString().split('T')[0]}.pdf`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success('MHD-Abschriften PDF erfolgreich heruntergeladen!');
      
    } catch (error) {
      console.error('MHD Export failed:', error);
      let errorMsg = 'Fehler beim Erstellen des Exports';
      if (error.response?.status === 404) {
        errorMsg = 'Keine MHD-Abschriften gefunden';
      }
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async (exportId) => {
    try {
      toast.info('PDF wird heruntergeladen...');
      
      const response = await axios.get(
        `${API}/api/backshop/exports/${exportId}/pdf`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          responseType: 'blob',
          timeout: 30000
        }
      );
      
      if (!response.data || response.data.size === 0) {
        throw new Error('PDF ist leer');
      }
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Backshop-Export-${exportId}.pdf`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success('PDF erfolgreich heruntergeladen!');
    } catch (error) {
      console.error('PDF download failed:', error);
      toast.error('Fehler beim Herunterladen des PDFs');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6 pb-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}}>
            Statistik & Export
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-2">
            Auswertungen und Exporte für Ihre Abschriften
          </p>
        </div>

        {/* Export Type Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base sm:text-lg">Abschriftart wählen</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={() => setExportType('backshop')}
                className={`p-3 sm:p-4 rounded-lg border-2 text-left transition-all ${
                  exportType === 'backshop'
                    ? 'bg-orange-50 border-orange-500'
                    : 'bg-white border-gray-300 hover:border-orange-400'
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <ShoppingBag className={`h-5 w-5 ${exportType === 'backshop' ? 'text-orange-600' : 'text-gray-600'}`} />
                  <span className="font-semibold text-sm sm:text-base">Backshop Abschriften</span>
                </div>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                  Exporte für Backshop und TGTG Abschriften mit Datumsfilter
                </p>
              </button>

              <button
                onClick={() => setExportType('mhd')}
                className={`p-3 sm:p-4 rounded-lg border-2 text-left transition-all ${
                  exportType === 'mhd'
                    ? 'bg-blue-50 border-blue-500'
                    : 'bg-white border-gray-300 hover:border-blue-400'
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <CalendarIcon className={`h-5 w-5 ${exportType === 'mhd' ? 'text-blue-600' : 'text-gray-600'}`} />
                  <span className="font-semibold text-sm sm:text-base">MHD Abschriften</span>
                </div>
                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                  Alle MHD-Abschriften (Mindesthaltbarkeitsdatum)
                </p>
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Backshop Export Section */}
        {exportType === 'backshop' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <ShoppingBag className="h-5 w-5 text-orange-600" />
                Backshop Abschriften exportieren
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Date Range */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label className="text-xs sm:text-sm">Von *</Label>
                  <Input
                    type="date"
                    value={backshopStartDate}
                    onChange={(e) => setBackshopStartDate(e.target.value)}
                    className="text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs sm:text-sm">Bis *</Label>
                  <Input
                    type="date"
                    value={backshopEndDate}
                    onChange={(e) => setBackshopEndDate(e.target.value)}
                    className="text-sm"
                  />
                </div>
              </div>

              {/* Type Selection */}
              <div className="space-y-2">
                <Label className="text-xs sm:text-sm">Typ *</Label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <button
                    onClick={() => setBackshopType('backshop')}
                    className={`px-3 py-2.5 rounded-lg border-2 text-xs sm:text-sm font-medium transition-all ${
                      backshopType === 'backshop'
                        ? 'bg-orange-500 text-white border-orange-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'
                    }`}
                  >
                    🏪 Nur Backshop
                  </button>
                  <button
                    onClick={() => setBackshopType('tgtg')}
                    className={`px-3 py-2.5 rounded-lg border-2 text-xs sm:text-sm font-medium transition-all ${
                      backshopType === 'tgtg'
                        ? 'bg-green-500 text-white border-green-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-green-400'
                    }`}
                  >
                    🌱 Nur TGTG
                  </button>
                  <button
                    onClick={() => setBackshopType('both')}
                    className={`px-3 py-2.5 rounded-lg border-2 text-xs sm:text-sm font-medium transition-all ${
                      backshopType === 'both'
                        ? 'bg-blue-500 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                    }`}
                  >
                    📦 Beide
                  </button>
                </div>
              </div>

              {/* Format Selection */}
              <div className="space-y-2">
                <Label className="text-xs sm:text-sm">Format *</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    onClick={() => setBackshopFormat('detailed')}
                    className={`p-3 sm:p-4 rounded-lg border-2 text-left transition-all ${
                      backshopFormat === 'detailed'
                        ? 'bg-blue-50 border-blue-500'
                        : 'bg-white border-gray-300 hover:border-blue-400'
                    }`}
                  >
                    <div className="font-medium text-xs sm:text-sm mb-1">📋 Detailliert</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      Tag für Tag mit Mitarbeiter
                    </div>
                  </button>
                  <button
                    onClick={() => setBackshopFormat('summary')}
                    className={`p-3 sm:p-4 rounded-lg border-2 text-left transition-all ${
                      backshopFormat === 'summary'
                        ? 'bg-blue-50 border-blue-500'
                        : 'bg-white border-gray-300 hover:border-blue-400'
                    }`}
                  >
                    <div className="font-medium text-xs sm:text-sm mb-1">📊 Zusammenfassung</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      Gesamtsummen pro Produkt
                    </div>
                  </button>
                </div>
              </div>

              {/* Export Button */}
              <Button
                onClick={handleBackshopExport}
                disabled={loading || !backshopStartDate || !backshopEndDate}
                className="w-full h-12 sm:h-14 bg-orange-600 hover:bg-orange-700 text-sm sm:text-base font-semibold"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 mr-2 animate-spin" />
                    Export wird erstellt...
                  </>
                ) : (
                  <>
                    <FileDown className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                    PDF Export erstellen & herunterladen
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* MHD Export Section */}
        {exportType === 'mhd' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
                <CalendarIcon className="h-5 w-5 text-blue-600" />
                MHD Abschriften exportieren
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                Exportiert alle MHD-Abschriften (Mindesthaltbarkeitsdatum) als PDF.
              </p>

              <div className="p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Package2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                    <p className="font-medium mb-1">Export-Inhalt:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Alle abgeschriebenen MHD-Produkte</li>
                      <li>Datum, Produkt, EAN/Art.Nr., Ablaufdatum</li>
                      <li>Stückzahl, Standort, Mitarbeiter</li>
                      <li>Gesamt-Statistik</li>
                    </ul>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleMHDExport}
                disabled={loading}
                className="w-full h-12 sm:h-14 bg-blue-600 hover:bg-blue-700 text-sm sm:text-base font-semibold"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 mr-2 animate-spin" />
                    Export wird erstellt...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                    MHD-Abschriften PDF herunterladen
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Info Box */}
        <Card>
          <CardContent className="pt-6">
            <div className="p-3 sm:p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300">
                <strong>💡 Tipp:</strong> Für erweiterte Export-Verwaltung, Historie und "Als erledigt"-Markierung für Backshop-Abschriften nutzen Sie die{' '}
                <a href="/admin/backshop-export" className="text-orange-600 hover:underline font-medium">
                  Abschrift-Export Seite
                </a>.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};
